/**
 * F-148 §5 / §9.2 post confirmation (AC13–AC16). Shows what will change, then "Posting…" and,
 * after a transport timeout, "Checking result…" while the same idempotency key is retried.
 * Success is shown only when the server returns a receipt (the route handles navigation).
 */
import { Loader2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  isRetryableReceivingError,
  receivingErrorKey,
  receivingErrorOptions,
} from '../lib/error-messages';

export type PostPhase = 'idle' | 'posting' | 'checking';

export interface PostConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly lineCount: number;
  readonly acceptedUnits: number;
  readonly phase: PostPhase;
  readonly error?: unknown;
  readonly onConfirm: () => void;
  readonly onRetry: () => void;
}

export function PostConfirmDialog({
  open,
  onOpenChange,
  lineCount,
  acceptedUnits,
  phase,
  error = null,
  onConfirm,
  onRetry,
}: PostConfirmDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const busy = phase !== 'idle';
  const hasError = error !== null && error !== undefined;
  const retryable = hasError && isRetryableReceivingError(error);

  return (
    <AlertDialog open={open} onOpenChange={(next) => (busy ? undefined : onOpenChange(next))}>
      <AlertDialogContent
        dir={dir}
        className="w-[calc(100vw-1.5rem)]"
        data-testid="post-confirm-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.receiving.review.confirmTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.receiving.review.confirmDescription', {
              lines: lineCount,
              accepted: acceptedUnits,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="min-h-5 text-start text-sm"
        >
          {phase === 'posting' ? (
            <span
              className="text-muted-foreground inline-flex items-center gap-2"
              data-testid="post-phase-posting"
            >
              <Loader2
                aria-hidden="true"
                className="size-4 animate-spin motion-reduce:animate-none"
              />
              {t('admin.receiving.review.posting')}
            </span>
          ) : null}
          {phase === 'checking' ? (
            <span
              className="text-muted-foreground inline-flex items-center gap-2"
              data-testid="post-phase-checking"
            >
              <Loader2
                aria-hidden="true"
                className="size-4 animate-spin motion-reduce:animate-none"
              />
              {t('admin.receiving.review.checking')}
            </span>
          ) : null}
          {phase === 'idle' && hasError ? (
            <span className="text-destructive" data-testid="post-error">
              {t(receivingErrorKey(error), receivingErrorOptions(error))}
            </span>
          ) : null}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel className="min-h-11" disabled={busy} data-testid="post-cancel">
            {t('common.cancel')}
          </AlertDialogCancel>
          {hasError && retryable ? (
            <Button
              type="button"
              className="min-h-11"
              disabled={busy}
              onClick={onRetry}
              data-testid="post-retry"
            >
              {t('common.retry')}
            </Button>
          ) : (
            <Button
              type="button"
              className="min-h-11"
              disabled={busy || (hasError && !retryable)}
              onClick={onConfirm}
              data-testid="post-confirm"
            >
              {t('admin.receiving.review.confirm')}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
